/**
 * useProjectTimeTracking - Implied time tracking when user is on a project in Project Hub
 * Counts time only while the tab is visible and there has been recent activity
 * (pointer/keyboard/scroll). Pauses when the user switches tabs or is idle 30s+.
 */

import { useRef } from 'react';
import { useAuth } from '../utils/AuthContext';
import { saveProjectTimeEntry } from '../utils/projectTimeTrackingService';
import type { ProjectPhase } from '../types';
import { isEarlyFunnelProjectPhase } from '../types';
import { useIdleAwareTimeSegments } from './useIdleAwareTimeSegments';

interface UseProjectTimeTrackingOptions {
  projectId: string | null;
  phase?: ProjectPhase | null;
  enabled?: boolean;
}

function resolvePhase(phase?: ProjectPhase | null): 'preconstruction' | 'contracted' {
  return isEarlyFunnelProjectPhase(phase) ? 'preconstruction' : 'contracted';
}

export function useProjectTimeTracking({
  projectId,
  phase,
  enabled = true,
}: UseProjectTimeTrackingOptions) {
  const { user } = useAuth();
  const userRef = useRef(user);
  const phaseRef = useRef(phase);
  userRef.current = user;
  phaseRef.current = phase;

  const { elapsedSeconds, flush } = useIdleAwareTimeSegments({
    enabled: enabled && Boolean(projectId) && Boolean(user?.id),
    sessionKey: projectId,
    trackElapsed: true,
    onSegment: async ({ start, end, minutes, sessionKey }) => {
      const currentUser = userRef.current;
      const currentProjectId = sessionKey || projectId;
      if (!currentProjectId || !currentUser?.id) return;
      await saveProjectTimeEntry({
        projectId: currentProjectId,
        userId: currentUser.id,
        userName: currentUser.name || 'Unknown',
        type: 'implied',
        minutes,
        date: start,
        startTime: start,
        endTime: end,
        phase: resolvePhase(phaseRef.current),
        createdAt: new Date(),
      });
    },
  });

  return { elapsedSeconds, flush };
}
